import type { ReactNode } from 'react';
import { type FieldValues, FormProvider, type UseFormReturn } from 'react-hook-form';

import { Drawer, type DrawerProps } from './drawer';

export type FormDrawerProps<T extends FieldValues> = Omit<
  DrawerProps,
  'onSubmit' | 'primaryText' | 'onPrimary' | 'primaryIsSubmit' | 'children'
> & {
  form: UseFormReturn<T>;
  onSubmit: (values: T) => void | Promise<void>;
  submitText?: string;
  resetOnClose?: boolean;
  children: ReactNode;
};

export function FormDrawer<T extends FieldValues>({
  form,
  onSubmit,
  submitText = 'Apply',
  resetOnClose = true,
  onClose,
  loading,
  maskClosable,
  children,
  ...rest
}: FormDrawerProps<T>) {
  const isDirty = form.formState.isDirty;
  const handleClose = () => {
    if (resetOnClose) form.reset();
    onClose();
  };

  return (
    <Drawer
      {...rest}
      onClose={handleClose}
      maskClosable={maskClosable ?? !isDirty}
      loading={loading || form.formState.isSubmitting}
      primaryText={submitText}
      primaryIsSubmit
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <FormProvider {...form}>{children}</FormProvider>
    </Drawer>
  );
}
